import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useMapsConfig } from '../../contexts/MapsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { SearchHeader, MapSection, AddressFormFields } from './address-picker';

const API = window.location.origin + '/api';

// CSS to ensure Google Places autocomplete dropdown appears above modal
const pacContainerStyle = document.createElement('style');
pacContainerStyle.textContent = `
  .pac-container {
    z-index: 99999 !important;
    background-color: white !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
    border: 1px solid #e5e7eb !important;
    margin-top: 4px !important;
    font-family: inherit !important;
  }
  .pac-item {
    padding: 12px 16px !important;
    cursor: pointer !important;
    border-top: 1px solid #f3f4f6 !important;
  }
  .pac-item:first-child {
    border-top: none !important;
  }
  .pac-item:hover {
    background-color: #fef3c7 !important;
  }
  .pac-item-query {
    font-size: 14px !important;
    color: #1f2937 !important;
  }
  .pac-matched {
    font-weight: 600 !important;
  }
  .pac-icon {
    margin-right: 12px !important;
  }
`;
if (!document.querySelector('style[data-pac-style]')) {
  pacContainerStyle.setAttribute('data-pac-style', 'true');
  document.head.appendChild(pacContainerStyle);
}

/**
 * Full-screen Address Picker Modal - Grab/Uber style
 * Features:
 * - Google Places Autocomplete for search
 * - Map-first UX with tap-to-pin
 * - Current location detection
 * - Reverse geocoding
 * - Address details at bottom
 */
export default function AddressPickerModal({ 
  open, 
  onClose, 
  onSave, 
  initialAddress = {} 
}) {
  const mapsConfig = useMapsConfig();
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const modalRef = useRef(null);
  
  // Map state
  const [mapCenter, setMapCenter] = useState([
    initialAddress.latitude || 14.5995,
    initialAddress.longitude || 120.9842
  ]);
  const [selectedLocation, setSelectedLocation] = useState({
    lat: initialAddress.latitude || null,
    lng: initialAddress.longitude || null
  });
  
  // Address state
  const [label, setLabel] = useState(initialAddress.label || '');
  const [streetAddress, setStreetAddress] = useState(initialAddress.full_address || '');
  const [province, setProvince] = useState(initialAddress.province || '');
  const [city, setCity] = useState(initialAddress.city || '');
  const [barangay, setBarangay] = useState(initialAddress.barangay || '');
  const [zipCode, setZipCode] = useState(initialAddress.zip_code || '');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // UI state
  const [isLocating, setIsLocating] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [placesLoaded, setPlacesLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  
  // Location dropdown data
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [barangays, setBarangays] = useState([]);

  // Wait for Google Maps to be loaded by UnifiedMap's useJsApiLoader
  useEffect(() => {
    if (!open || mapsConfig.provider !== 'google') return;
    
    // Check periodically for Google Maps to be loaded by UnifiedMap
    const checkGoogleLoaded = () => {
      if (window.google?.maps?.places) {
        setPlacesLoaded(true);
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkGoogleLoaded()) return;
    
    // Poll for a while
    const interval = setInterval(() => {
      if (checkGoogleLoaded()) {
        clearInterval(interval);
      }
    }, 200);
    
    // Give up after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      console.warn('[AddressPickerModal] Google Maps Places not loaded after timeout');
    }, 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [open, mapsConfig.provider]);

  // Mark map as ready after a short delay for rendering
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setMapReady(true), 500);
      return () => clearTimeout(timer);
    } else {
      setMapReady(false);
    }
  }, [open]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!placesLoaded || !searchInputRef.current || !window.google?.maps?.places) {
      console.log('[AddressPickerModal] Cannot init autocomplete:', { placesLoaded, hasRef: !!searchInputRef.current, hasPlaces: !!window.google?.maps?.places });
      return;
    }
    
    console.log('[AddressPickerModal] Initializing Google Places Autocomplete');
    
    // Clean up previous autocomplete
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    // Small delay to ensure DOM is ready
    const initTimeout = setTimeout(() => {
      try {
        // Create new autocomplete
        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          componentRestrictions: { country: 'ph' },
          fields: ['geometry', 'formatted_address', 'address_components', 'name'],
          types: ['geocode', 'establishment']
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          console.log('[AddressPickerModal] Place selected:', place?.name);
          
          if (!place.geometry?.location) {
            toast.error('Could not find location details');
            return;
          }

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          setSelectedLocation({ lat, lng });
          setMapCenter([lat, lng]);
          setSearchQuery(place.name || place.formatted_address || '');
          setShowSearchResults(false);

          // Parse address components
          if (place.address_components) {
            parseAddressComponents(place.address_components, place.formatted_address);
          }

          toast.success('Location selected!');
        });

        autocompleteRef.current = autocomplete;
        console.log('[AddressPickerModal] Autocomplete initialized successfully');
      } catch (err) {
        console.error('[AddressPickerModal] Failed to initialize autocomplete:', err);
      }
    }, 100);

    return () => {
      clearTimeout(initTimeout);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [placesLoaded, open]);

  // Parse Google address components into our fields
  const parseAddressComponents = (components, formattedAddress) => {
    let street = '';
    let routeName = '';
    let locality = '';
    let sublocality = '';
    let adminArea1 = '';
    let adminArea2 = '';
    let postalCode = '';

    components.forEach(comp => {
      const types = comp.types;
      if (types.includes('street_number')) {
        street = comp.long_name;
      }
      if (types.includes('route')) {
        routeName = comp.long_name;
      }
      if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
        sublocality = comp.long_name;
      }
      if (types.includes('locality')) {
        locality = comp.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        adminArea2 = comp.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        adminArea1 = comp.long_name;
      }
      if (types.includes('postal_code')) {
        postalCode = comp.long_name;
      }
    });

    // Set address fields
    if (street || routeName) {
      setStreetAddress(`${street} ${routeName}`.trim());
    }
    if (sublocality) {
      setBarangay(sublocality);
    }
    if (locality || adminArea2) {
      setCity(locality || adminArea2);
    }
    if (adminArea1) {
      setProvince(adminArea1);
    }
    if (postalCode) {
      setZipCode(postalCode);
    }
  };

  // Fallback search using Nominatim (if Google Places not available)
  const handleFallbackSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setShowSearchResults(true);
    try {
      const res = await axios.get(`${API}/locations/geocode?address=${encodeURIComponent(searchQuery + ', Philippines')}`);
      setSearchResults(res.data?.results || []);
      if (res.data?.results?.length === 0) {
        toast.info('No results found. Try a more specific address.');
      }
    } catch (err) {
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Select fallback search result
  const selectSearchResult = (result) => {
    const { latitude, longitude } = result;
    setSelectedLocation({ lat: latitude, lng: longitude });
    setMapCenter([latitude, longitude]);
    setSearchQuery(result.display_name?.split(',')[0] || '');
    setShowSearchResults(false);
    reverseGeocode(latitude, longitude);
  };

  // Fetch provinces on mount
  useEffect(() => {
    if (open) {
      axios.get(`${API}/locations/provinces`)
        .then(res => setProvinces(res.data || []))
        .catch(() => {});
    }
  }, [open]);

  // Fetch cities when province changes
  useEffect(() => {
    if (province) {
      axios.get(`${API}/locations/cities?province=${encodeURIComponent(province)}`)
        .then(res => setCities(res.data?.map(c => c.city || c) || []))
        .catch(() => setCities([]));
    } else {
      setCities([]);
    }
  }, [province]);

  // Fetch barangays when city changes
  useEffect(() => {
    if (city) {
      axios.get(`${API}/locations/barangays?city=${encodeURIComponent(city)}`)
        .then(res => setBarangays(res.data?.map(b => b.barangay || b) || []))
        .catch(() => setBarangays([]));
    } else {
      setBarangays([]);
    }
  }, [city]);

  // Reverse geocode when map is clicked/pin is moved
  const reverseGeocode = useCallback(async (lat, lng) => {
    setIsReverseGeocoding(true);
    try {
      // Try Google Geocoder first if available
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            parseAddressComponents(results[0].address_components, results[0].formatted_address);
            setSearchQuery(results[0].formatted_address?.split(',')[0] || '');
          }
          setIsReverseGeocoding(false);
        });
        return;
      }
      
      // Fallback to our API
      const res = await axios.get(`${API}/locations/reverse-geocode?lat=${lat}&lng=${lng}`);
      const data = res.data;
      if (data?.address) {
        const addr = data.address;
        if (addr.state || addr.region) setProvince(addr.state || addr.region || '');
        if (addr.city || addr.town || addr.municipality) setCity(addr.city || addr.town || addr.municipality || '');
        if (addr.suburb || addr.neighbourhood || addr.village) setBarangay(addr.suburb || addr.neighbourhood || addr.village || '');
        if (addr.road) setStreetAddress(prev => prev || addr.road);
        if (addr.postcode) setZipCode(addr.postcode);
      }
    } catch (err) {
      console.log('Reverse geocode failed:', err);
    } finally {
      setIsReverseGeocoding(false);
    }
  }, []);

  // Handle map click
  const handleMapClick = (lat, lng) => {
    setSelectedLocation({ lat, lng });
    setMapCenter([lat, lng]);
    reverseGeocode(lat, lng);
  };

  // Detect current location
  const detectCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedLocation({ lat: latitude, lng: longitude });
        setMapCenter([latitude, longitude]);
        reverseGeocode(latitude, longitude);
        setIsLocating(false);
        toast.success('Location detected!');
      },
      (error) => {
        setIsLocating(false);
        toast.error('Could not detect location. Please allow location access.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [reverseGeocode]);

  // Validate form
  const isFormValid = () => {
    return selectedLocation.lat && selectedLocation.lng && label.trim() && streetAddress.trim() && city.trim();
  };

  // Handle save
  const handleSave = () => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    onSave({
      label: label.trim(),
      full_address: streetAddress.trim(),
      province,
      city,
      barangay,
      zip_code: zipCode,
      latitude: selectedLocation.lat,
      longitude: selectedLocation.lng
    });
  };

  // Auto-detect location on first open
  useEffect(() => {
    if (open && !selectedLocation.lat && !initialAddress.latitude) {
      detectCurrentLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const isGooglePlacesAvailable = placesLoaded && mapsConfig.provider === 'google';

  // Handle close with event stop
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  // Handle toggle with event stop
  const handleToggleDetails = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDetails(prev => !prev);
  };

  return (
    <div 
      ref={modalRef}
      className="fixed inset-0 bg-white flex flex-col" 
      data-testid="address-picker-modal"
      style={{ 
        height: '100dvh', 
        maxHeight: '100dvh',
        zIndex: 9999,
        isolation: 'isolate'
      }}
    >
      {/* Header with search */}
      <SearchHeader
        ref={searchInputRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearching={isSearching}
        showSearchResults={showSearchResults}
        searchResults={searchResults}
        isGooglePlacesAvailable={isGooglePlacesAvailable}
        onClose={handleClose}
        onSelectResult={selectSearchResult}
        onFallbackSearch={handleFallbackSearch}
      />

      {/* Map area - takes most of the screen */}
      <MapSection
        mapCenter={mapCenter}
        mapReady={mapReady}
        selectedLocation={selectedLocation}
        isLocating={isLocating}
        isReverseGeocoding={isReverseGeocoding}
        onMapClick={handleMapClick}
        onDetectLocation={detectCurrentLocation}
      />

      {/* Bottom sheet with address details */}
      <div 
        className="flex-shrink-0 bg-white border-t border-stone-200 flex flex-col"
        style={{ 
          maxHeight: showDetails ? '65dvh' : '200px',
          minHeight: showDetails ? '300px' : '180px',
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          zIndex: 30
        }}
      >
        {/* Toggle details button */}
        <button
          type="button"
          onClick={handleToggleDetails}
          className="flex-shrink-0 w-full py-3 flex items-center justify-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border-b border-stone-100 transition-colors"
          data-testid="toggle-address-details-btn"
          style={{ touchAction: 'manipulation' }}
        >
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${showDetails ? 'rotate-180' : ''}`} />
          {showDetails ? 'Hide address details' : 'Show address details'}
        </button>

        {/* Scrollable form fields - with extra padding at bottom for fixed button */}
        <div 
          className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-3"
          style={{ paddingBottom: '90px' }}
        >
          <AddressFormFields
            selectedLocation={selectedLocation}
            label={label}
            setLabel={setLabel}
            streetAddress={streetAddress}
            setStreetAddress={setStreetAddress}
            showDetails={showDetails}
            province={province}
            onProvinceChange={(v) => { setProvince(v); setCity(''); setBarangay(''); }}
            city={city}
            onCityChange={(v) => { setCity(v); setBarangay(''); }}
            barangay={barangay}
            setBarangay={setBarangay}
            zipCode={zipCode}
            setZipCode={setZipCode}
            provinces={provinces}
            cities={cities}
            barangays={barangays}
          />
        </div>
      </div>

      {/* FIXED Confirm Button - Always visible at bottom */}
      <div 
        className="bg-white border-t border-stone-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
        style={{ 
          padding: '12px 16px',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          position: 'relative',
          zIndex: 40,
          flexShrink: 0
        }}
      >
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isFormValid()}
          className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="confirm-address-btn"
        >
          {!selectedLocation.lat ? 'Tap map to set location' : !isFormValid() ? 'Fill required fields' : 'Confirm Location'}
        </Button>
      </div>
    </div>
  );
}
